import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, TenantMembership } from '../auth.service';

const REMEMBER_KEY = 'centaiva.remember';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss',
})
export class LoginPageComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly showPassword = signal(false);
  readonly tenants = signal<TenantMembership[]>([]);
  readonly selectingTenantId = signal<string | null>(null);
  readonly currentYear = new Date().getFullYear();

  readonly form = this.fb.nonNullable.group({
    emailOrUserName: ['', Validators.required],
    password: ['', Validators.required],
    rememberMe: [false],
  });

  ngOnInit(): void {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) {
      const { emailOrUserName } = JSON.parse(saved) as { emailOrUserName: string };
      this.form.patchValue({ emailOrUserName, rememberMe: true });
    }
  }

  submit(): void {
    if (this.form.invalid || this.loading()) return;

    this.loading.set(true);
    this.error.set(null);
    const value = this.form.getRawValue();

    this.auth.login({
      emailOrUserName: value.emailOrUserName,
      password: value.password,
    }).subscribe({
      next: (result) => {
        if (value.rememberMe) {
          localStorage.setItem(REMEMBER_KEY, JSON.stringify({ emailOrUserName: value.emailOrUserName }));
        } else {
          localStorage.removeItem(REMEMBER_KEY);
        }
        this.loading.set(false);
        if (result.session) {
          this.router.navigateByUrl('/dashboard');
          return;
        }

        const tenants = result.tenants;
        if (tenants.length === 1) {
          this.selectTenant(tenants[0]);
          return;
        }

        this.tenants.set(tenants);
      },
      error: () => {
        this.error.set('Invalid credentials.');
        this.loading.set(false);
      },
    });
  }

  selectTenant(tenant: TenantMembership): void {
    if (this.selectingTenantId()) return;
    const tenantId = tenant.tenantId ?? tenant.tenant_id ?? tenant.id ?? null;
    this.selectingTenantId.set(tenantId);
    this.error.set(null);
    this.auth.selectTenant(tenant).subscribe({
      next: () => this.router.navigateByUrl('/dashboard'),
      error: (err) => {
        this.error.set(err?.error?.message ?? err?.message ?? 'Unable to select this tenant.');
        this.selectingTenantId.set(null);
      },
    });
  }

  tenantName(tenant: TenantMembership): string {
    return tenant.displayName ?? tenant.tenantName ?? tenant.name ?? tenant.tenantId ?? tenant.id ?? 'Unnamed tenant';
  }

  closeTenantModal(): void {
    if (!this.selectingTenantId()) this.tenants.set([]);
  }
}
